import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';

interface OnboardingFooterProps {
  completedTasks: number;
  totalTasks: number;
  discountAmount?: number;
  applyDiscount?: boolean;
}

/**
 * OnboardingFooter Component
 *
 * A modern footer component for the onboarding checklist card.
 * Displays a purple progress bar with percentage and optional discount text.
 */
export const OnboardingFooter: React.FC<OnboardingFooterProps> = ({
  completedTasks,
  totalTasks,
  discountAmount,
  applyDiscount: _applyDiscount,
}) => {
  const progressPercentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  return (
    <Box>
      {/* Main text */}
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          mb: 1.5,
          color: 'text.primary',
        }}
      >
        Complete & get ${discountAmount ? discountAmount / 100 : 50} off next month!
      </Typography>

      {/* Progress bar with percentage */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flex: 1, position: 'relative' }}>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'action.hover',
              outline: 'solid',
              outlineOffset: '-1px',
              outlineWidth: '1px',
              outlineColor: 'rgba(0, 17, 102, 0.10)',
              boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.08) inset',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'primary.main',
                borderRadius: 4,
              },
            }}
          />
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            minWidth: '40px',
            textAlign: 'right',
          }}
        >
          {Math.round(progressPercentage)}%
        </Typography>
      </Box>
    </Box>
  );
};

export default OnboardingFooter;
